import { useEffect, useMemo, useRef, useState } from 'react';

import {
  Box,
  PhotoCarousel as BricksPhotoCarousel,
  CloseButton,
  PhotoCarouselAspectRatios,
  ShareButton,
} from '@opendoor/bricks-next';

import { MOBILE_CAROUSEL_HEIGHT_MD, MOBILE_CAROUSEL_HEIGHT_SM } from './types';

interface PhotoCarouselProps {
  photos: string[] | null | undefined;
  onClose: () => void;
  onOpenShareModal: () => void;
  isRounded?: boolean;
  height?: number;
  onPhotoClick?: () => void;
}

// Carousel nav arrows are hidden below this viewport width because they
// overlap the photo on narrow phones.
const HIDE_NAVIGATION_MAX_WIDTH_PX = 600;

type PresetTier = {
  minWidth: number;
  maxWidth: number | null;
  preset: string;
  presetScale: string;
};

// Mirrors the standalone PDP desktop gallery (GalleryPhotos
// GALLERY_SOURCE_SET_CONFIG) so the mini PDP carousel renders at parity on
// desktop. The <1201 tier matches the standalone PDP mobile carousel
// (components/marketplace/property/media/PhotoCarousel.tsx).
const PRESET_TIERS: PresetTier[] = [
  { minWidth: 0, maxWidth: 1201, preset: '3:2-md', presetScale: '2/3' },
  { minWidth: 1201, maxWidth: 1400, preset: '3:2-lg', presetScale: '3/4' },
  { minWidth: 1400, maxWidth: null, preset: '3:2-xl', presetScale: '1' },
];

// Below this viewport width the standalone PDP carousel pins dpr=1 to keep
// cellular payloads bounded; above it we follow the standalone PDP gallery's
// [1, 2, 3] srcset ladder by selecting the actual devicePixelRatio.
// Intentionally aligned with the first PRESET_TIERS breakpoint so the "mobile
// tier" and "mobile dpr cap" move together.
const MOBILE_DPR_CAP_MAX_WIDTH_PX = 1201;

const pickPresetTier = (viewportWidth: number): PresetTier =>
  PRESET_TIERS.find(
    (tier) =>
      viewportWidth >= tier.minWidth && (tier.maxWidth == null || viewportWidth < tier.maxWidth),
  ) ?? PRESET_TIERS[0];

const buildPhotoUrl = (photo: string, viewportWidth: number, devicePixelRatio: number): string => {
  const tier = pickPresetTier(viewportWidth);
  const dpr =
    viewportWidth < MOBILE_DPR_CAP_MAX_WIDTH_PX
      ? 1
      : Math.max(1, Math.min(3, Math.round(devicePixelRatio)));
  const params = new URLSearchParams({
    preset: tier.preset,
    presetScale: tier.presetScale,
    service: 'cosmos',
    auto: 'format,compress',
    dpr: String(dpr),
  });
  return `${photo}?${params.toString()}`;
};

export const PhotoCarousel = ({
  photos,
  onClose,
  isRounded = false,
  onOpenShareModal,
  onPhotoClick,
  height,
}: PhotoCarouselProps) => {
  const [shouldHideNavigation, setShouldHideNavigation] = useState(false);
  // SSR default renders the mobile-equivalent URL (dpr=1, 3:2-md@2/3), matching
  // prior behavior. The real viewport/dpr are read on mount and the carousel
  // upgrades to the standalone PDP gallery preset on desktop / Retina.
  //
  // Known tradeoff: a desktop-Retina visitor fetches the dpr=1 URL during SSR
  // paint and then the dpr=2 upgrade URL after hydration (double fetch). We
  // accept this because (a) mini PDPs open inside an already-interactive SPA
  // so the SSR image rarely appears at all for most users, (b) pinning the
  // SSR default higher would inflate mobile payloads (the common case), and
  // (c) a `srcset`-based fix would require upstream changes in
  // bricks-next PhotoCarousel. Revisit if LCP regressions surface.
  const [viewport, setViewport] = useState<{ width: number; dpr: number }>({
    width: 1024,
    dpr: 1,
  });
  const carouselRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updateViewport = () => {
      setViewport({
        width: window.innerWidth,
        dpr: window.devicePixelRatio || 1,
      });
    };
    updateViewport();
    window.addEventListener('resize', updateViewport);
    return () => window.removeEventListener('resize', updateViewport);
  }, []);

  const mappedPhotos = useMemo(
    () =>
      photos?.map((photo) => ({
        url: buildPhotoUrl(photo, viewport.width, viewport.dpr),
        alt: '',
      })) || [],
    [photos, viewport.width, viewport.dpr],
  );

  useEffect(() => {
    const carousel = carouselRef.current;
    let startY = 0;
    let startX = 0;

    const handleTouchStart = (e: TouchEvent) => {
      startY = e.touches[0].clientY; // Set start Y to the initial touch Y position
      startX = e.touches[0].clientX; // Set start X to the initial touch X position
    };

    const handleTouchMove = (e: TouchEvent) => {
      // Calculate the difference between current touch position and start position
      const moveY = e.touches[0].clientY - startY;
      const moveX = e.touches[0].clientX - startX;

      // We're doing this because we want to allow vertical scrolling
      // if the user starts scrolling vertically first
      // But if they start scrolling horizontally first, we want to prevent vertical scrolling
      if (Math.abs(moveX) > Math.abs(moveY)) {
        e.preventDefault(); // Prevent Vertical scrolling
      } else {
        e.stopPropagation(); // Prevent Horizontal scrolling
      }
    };

    if (carousel) {
      carousel.addEventListener('touchstart', handleTouchStart, { passive: true });
      carousel.addEventListener('touchmove', handleTouchMove, { passive: false });
    }

    return () => {
      if (carousel) {
        // Clean up event listeners
        carousel.removeEventListener('touchstart', handleTouchStart);
        carousel.removeEventListener('touchmove', handleTouchMove);
      }
    };
  }, [carouselRef]);

  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel || !onPhotoClick) {
      return;
    }

    // Use `closest('img')` instead of `event.target.tagName === 'IMG'` so the
    // handler still fires when the click lands on a descendant of the image
    // (e.g. a future overlay/badge or a wrapper span added by the carousel).
    const handleClick = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }
      if (target.closest('img')) {
        onPhotoClick();
      }
    };

    carousel.addEventListener('click', handleClick);

    return () => {
      carousel.removeEventListener('click', handleClick);
    };
  }, [onPhotoClick]);

  useEffect(() => {
    const updateNavVisibility = () => {
      setShouldHideNavigation(window.innerWidth < HIDE_NAVIGATION_MAX_WIDTH_PX);
    };

    updateNavVisibility();
    window.addEventListener('resize', updateNavVisibility);
    return () => window.removeEventListener('resize', updateNavVisibility);
  }, []);

  return (
    <Box
      position="relative"
      aspectRatio={PhotoCarouselAspectRatios.SQUARE}
      justifyContent="center"
      overflow="hidden"
      width="100%"
      ref={carouselRef}
      $smallerThanMD={{ height: MOBILE_CAROUSEL_HEIGHT_SM }}
      $smallerThanLG={{ height: MOBILE_CAROUSEL_HEIGHT_MD }}
      // The base SQUARE aspectRatio is overridden on mobile by the fixed
      // heights above. On desktop (>= LG breakpoint = 1201px) the panel is
      // ~50% of the viewport width, so SQUARE would push the carousel to
      // ~100vh. Match the standalone PDP GalleryPhotos hero split at 16:9
      // instead. Uses $largerThanMD because in bricks-next media.ts,
      // `largerThanMD` = minWidth LG (1201px); `largerThanLG` = minWidth XL
      // (1400px), which would leave 1201-1399px viewports stuck at SQUARE
      // despite PRESET_TIERS / MOBILE_DPR_CAP_MAX_WIDTH_PX already treating
      // them as desktop.
      $largerThanMD={{ mt: 0, aspectRatio: PhotoCarouselAspectRatios.DEFAULT }}
    >
      <CloseButton
        id="close-button"
        size="large"
        onPress={onClose}
        analyticsName="marketplace-carousel-back-button"
        top="$8x"
        left={24}
        position="absolute"
        zIndex={1}
        data-testid="map-pdp-close-button"
      />
      <ShareButton
        id="share-button"
        size="large"
        onPress={onOpenShareModal}
        analyticsName="marketplace-carousel-share-button"
        top="$8x"
        right={24}
        position="absolute"
        zIndex={1}
      />
      <BricksPhotoCarousel
        photos={mappedPhotos}
        hideNavigation={shouldHideNavigation}
        // BricksPhotoCarousel exposes a single, non-media-query `aspectRatio`
        // prop. Mirror the outer Box's responsive aspect ratio here off the
        // same `viewport.width` we already track for photo-URL selection so
        // the inner carousel stays congruent with its container. Without this
        // the inner box renders SQUARE (e.g. 640x640) inside a 16:9 outer
        // (640x360) and the image overflows / looks cropped on desktop. Same
        // 1201 split point as PRESET_TIERS / $largerThanMD.
        aspectRatio={
          viewport.width >= MOBILE_DPR_CAP_MAX_WIDTH_PX
            ? PhotoCarouselAspectRatios.DEFAULT
            : PhotoCarouselAspectRatios.SQUARE
        }
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        height={height || '100%'}
        isRounded={isRounded || false}
      ></BricksPhotoCarousel>
    </Box>
  );
};
